'use client'

import React, { useState, useEffect } from 'react'
import styles from './styles.module.css'

interface HeroProps {
  onConsultationClick: () => void
}

const Hero: React.FC<HeroProps> = ({ onConsultationClick }) => {
  const [windowWidth, setWindowWidth] = useState(1440)
  const [hoveredCircle, setHoveredCircle] = useState<number | null>(null)

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Smooth transition from 768px to 1439px (only for tablets)
  const ratio = Math.max(0, Math.min(1, (windowWidth - 768) / (1440 - 768)))
  const circle2Left = windowWidth >= 768 && windowWidth <= 1439 ? 8.75 + (23.4375 - 8.75) * ratio : undefined
  const circle3Left = windowWidth >= 768 && windowWidth <= 1439 ? 20 + (46.875 - 20) * ratio : undefined
  
  // StepTabs positioning (only for tablets 768-1439px)
  const stepTabsLeft = windowWidth >= 768 && windowWidth <= 1439 ? 1.25 + (2.375 - 1.25) * ratio : undefined
  
  // StepRow positioning (only for tablets 768-1439px) - растягиваем лесенкой вправо
  const stepRow1Left = windowWidth >= 768 && windowWidth <= 1439 ? 0.21 + (0.21 * ratio) : undefined
  const stepRow2Left = windowWidth >= 768 && windowWidth <= 1439 ? 2 + (2 * ratio) : undefined
  const stepRow3Left = windowWidth >= 768 && windowWidth <= 1439 ? 7.75 + (7.75 * ratio) : undefined
  const stepRow4Left = windowWidth >= 768 && windowWidth <= 1439 ? 5.625 + (5.625 * ratio) : undefined
  const stepRow5Left = windowWidth >= 768 && windowWidth <= 1439 ? 22 + (22 * ratio) : undefined
  const stepRow6Left = windowWidth >= 768 && windowWidth <= 1439 ? 27 + (25 * ratio) : undefined
  
  // StepTab individual positioning (only for tablets 768-1439px) - растягиваем лесенкой вправо
  const stepTabGoalsLeft = windowWidth >= 768 && windowWidth <= 1439 ? 2 + (2 * ratio) : undefined
  const stepTabDesignLeft = windowWidth >= 768 && windowWidth <= 1439 ? 8.35 + (8.35 * ratio) : undefined
  const stepTabDevelopmentLeft = windowWidth >= 768 && windowWidth <= 1439 ? 13.35 + (13.35 * ratio) : undefined
  
  // Mobile dynamic positioning (767px to 360px)
  const mobileRatio = windowWidth <= 767 ? Math.max(0, Math.min(1, (767 - windowWidth) / (767 - 360))) : 0
  
  // Set CSS variables for tablet dynamics
  const tabletStyles = windowWidth >= 768 && windowWidth <= 1439 ? {
    '--step-row-1-left': `${stepRow1Left}rem`,
    '--step-row-2-left': `${stepRow2Left}rem`,
    '--step-row-3-left': `${stepRow3Left}rem`,
    '--step-row-4-left': `${stepRow4Left}rem`,
    '--step-row-5-left': `${stepRow5Left}rem`,
    '--step-row-6-left': `${stepRow6Left}rem`,
    '--step-tab-goals-left': `${stepTabGoalsLeft}rem`,
    '--step-tab-design-left': `${stepTabDesignLeft}rem`,
    '--step-tab-development-left': `${stepTabDevelopmentLeft}rem`,
  } as React.CSSProperties : {}
  
  return (
    <section className={styles.hero} style={tabletStyles}>
      <div className={styles.container}>
        {/* Main heading */}
        <div className={styles.content}>
          <h1 className={styles.title}>
            Цифровая трансформация вашего бизнеса
          </h1>
          
          {/* Subtitle with arrow and button */}
          <div className={styles.subtitleContainer}>
            <div className={styles.subtitle}>
              <span className={styles.subtitleText}>от идеи</span>
              <div className={styles.arrow}>
                <svg width="48" height="16" viewBox="0 0 48 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M39.9287 0.928731C40.3191 0.538306 40.9522 0.538504 41.3428 0.928731L47.707 7.29299C48.0976 7.68351 48.0976 8.31653 47.707 8.70705L41.3428 15.0713C40.9522 15.4615 40.3191 15.4617 39.9287 15.0713C39.5383 14.6809 39.5385 14.0478 39.9287 13.6572L44.5859 9.00002L0 9.00002L0 7.00002L44.5859 7.00002L39.9287 2.34279C39.5385 1.95225 39.5383 1.31916 39.9287 0.928731Z" fill="#F23B67"/>
                </svg>
              </div>
              <span className={styles.subtitleText}>до результата</span>
            </div>
            
            <button 
              className={styles.button}
              onClick={onConsultationClick}
            >
              <span className={styles.buttonText}>Получить консультацию</span>
              <svg className={styles.buttonIcon} viewBox="0 0 16 16" fill="none">
                <path d="M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z" fill="white" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Development Process Scheme */}
        <div className={styles.processScheme}>
          {/* Stage circles */}
          <div className={styles.stageCircles}>
            <div 
              className={styles.stageCircle}
              onMouseEnter={() => setHoveredCircle(1)}
              onMouseLeave={() => setHoveredCircle(null)}
            >
              <div className={styles.stageText}>Исследование</div>
            </div>
            <div 
              className={styles.stageCircle}
              style={circle2Left !== undefined ? { left: `${circle2Left}rem` } : {}}
              onMouseEnter={() => setHoveredCircle(2)}
              onMouseLeave={() => setHoveredCircle(null)}
            >
              <div className={styles.stageText}>Проектирование</div>
            </div>
            <div 
              className={styles.stageCircle}
              style={circle3Left !== undefined ? { left: `${circle3Left}rem` } : {}}
              onMouseEnter={() => setHoveredCircle(3)}
              onMouseLeave={() => setHoveredCircle(null)}
            >
              <div className={styles.stageText}>Разработка и запуск</div>
            </div>
          </div>
          
          {/* Step tabs */}
          <div className={styles.stepTabs} style={stepTabsLeft ? { marginLeft: `${stepTabsLeft}rem` } : { marginLeft: 'unset' }}>
            <div className={styles.stepRow}>
              <div className={`${styles.stepTab} ${styles.stepTabActive} ${hoveredCircle === 1 ? styles.stepTabHighlighted : ''}`}>
                <span>Идея</span>
              </div>
            </div>
            <div className={styles.stepRow}>
              <div className={`${styles.stepTab} ${styles.stepTabResearch} ${hoveredCircle === 1 ? styles.stepTabHighlighted : ''}`}>
                <span>Исследование</span>
              </div>
              <div className={`${styles.stepTab} ${styles.stepTabGoals} ${hoveredCircle === 1 ? styles.stepTabHighlighted : ''}`}>
                <span>Определение целей</span>
              </div>
            </div>
            <div className={styles.stepRow}>
              <div className={`${styles.stepTab} ${hoveredCircle === 2 ? styles.stepTabHighlighted : ''}`}>
                <span>Построение стратегии</span>
              </div>
            </div>
            <div className={styles.stepRow}>
              <div className={`${styles.stepTab} ${styles.stepTabDesign} ${hoveredCircle === 2 ? styles.stepTabHighlighted : ''}`}>
                <span>Проектирование</span>
              </div>
              <div className={`${styles.stepTab} ${styles.stepTabDevelopment} ${hoveredCircle === 3 ? styles.stepTabHighlighted : ''}`}>
                <span>Разработка</span>
              </div>
            </div>
            <div className={styles.stepRow}>
              <div className={`${styles.stepTab} ${hoveredCircle === 3 ? styles.stepTabHighlighted : ''}`}>
                <span>Тестирование</span>
              </div>
            </div>
            <div className={styles.stepRow}>
              <div className={`${styles.stepTab} ${styles.stepTabActive} ${hoveredCircle === 3 ? styles.stepTabHighlighted : ''}`}>
                <span>Запуск и техподдержка</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

Hero.displayName = 'Hero'

export default Hero